import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  Camera, CheckCircle2, Lock, CircleHelp, CornerDownRight, EyeOff, Flag, Hash, Heart, ImagePlus, LifeBuoy, Link2,
  MoreHorizontal, Pin, PlayCircle, Plus, Send, ShieldCheck, Smile, Sparkles, Star, Trash2, Undo2,
} from "lucide-react";
import {
  Avatar, Btn, CHANNELS, Card, Chip, Empty, Hand, IconBtn, List, ME, MESSAGES, Name, P, PLANS, PLink, PROJECTS, Polaroid, PortalShell, QA, Row, Segmented, Sheet,
  TierLock, Thumb, ago, who, fmtDay, fmtT, getProject, getSession, makesAt, messagesAt, sessionsFor, usePortal,
  type Channel, type Make, type Message, type Question, type TapeColor,
} from "./Portal";
import { useToast } from "./Join";
import "./Community.css";

/* /blue-2/portal/community — structured spaces (research §5 "Where things live").

   Three channels instead of Patreon's one quiet room:
   - #show-your-makes: a photo grid of taped polaroids, filterable by project,
     with a share-a-make sheet (local only).
   - #help: Q&A threads with an Open / Answered state; Chelsea's answer is
     marked, and a replay timestamp links straight to that moment.
   - #general: a simple chat stream with reactions.
   Each project deep-links here: community?ch=makes&project=witch-hat and
   community?ch=help&project=witch-hat. Chelsea gets moderation affordances
   (answer & resolve, turn into a fix note, pin, hide, delete).

   Phone: segmented channel tabs, then the list; a thread opens full-width.
   Desktop: two panes, the channel navigator (channels, filters, threads) on
   the left and the gallery, thread or chat on the right.

   Locks name the tier: reading needs a free account, posting/sharing needs
   Thread Friend ($3), asking a question needs Member ($25). */

type Ch = Channel["id"];
const isCh = (s: string | null): s is Ch => s === "makes" || s === "help" || s === "general";
const CH_ICON: Record<Ch, ReactNode> = { makes: <Camera size={18} aria-hidden="true" />, help: <LifeBuoy size={18} aria-hidden="true" />, general: <Hash size={18} aria-hidden="true" /> };
const CH_SHORT: Record<Ch, string> = { makes: "Makes", help: "Help", general: "General" };

/* ─────────── Extra sample data (community-only) ─────────── */

const h2 = MESSAGES.find(m => m.id === "h2")!;
const plus = (d: Date, min: number) => new Date(d.getTime() + min * 6e4);
const GENERAL_SEWING = "general-sewing";
/** #help threads not tied to a project (from the #help chat messages). */
const EXTRA_QS: Question[] = [
  { id: "h-needle", projectId: GENERAL_SEWING, by: h2.by, at: h2.at, votes: 3, text: "My machine skips stitches on the foam. What needle size should I use?",
    replies: [{ by: "bev", at: plus(h2.at, 35), text: "A 90/14 topstitch needle fixed it for me. Lengthen your stitch a little too." },
      { by: "noor", at: plus(h2.at, 80), text: "Walking foot helped me more than the needle!" }] },
];
const MAKE_COMMENTS: Record<string, { by: string; text: string; min: number }[]> = {
  mk1: [{ by: "maggie", text: "The spiders!! I need this", min: 40 }, { by: "tasha", text: "Ok now I want a second hat", min: 95 }],
  mk2: [{ by: "chelsea", text: "That quilting is so even, Bev!", min: 60 }],
  mk4: [{ by: "chelsea", text: "Gingham witch is my new favorite thing", min: 25 }, { by: "dee", text: "Gingham witch forever", min: 50 }],
  mk7: [{ by: "chelsea", text: "Rosie, this is perfect for fall!", min: 120 }],
};
const REPLIES: Record<string, { by: string; text: string; min: number }[]> = {
  g1: [{ by: "tasha", text: "Me! Driving up with my sister", min: 30 }, { by: "bev", text: "Wouldn't miss it", min: 45 }, { by: "chelsea", text: "Can't wait to see you all in person!!", min: 90 }],
  g2: [{ by: "maggie", text: "Which shop? I need those bats", min: 20 }],
};
const SAMPLE_PHOTOS = ["/chelsea-and-cosmo.jpg", "/concept/gingham-top.jpg", "/concept/label.jpg", "/img/products/qs-mat.jpg"];
const TAPES: TapeColor[] = ["pink", "yellow", "green", "peri"];
const TILTS = [-1.6, 1.2, -.6, 1.8, -1.1, .7];

/* ─────────── Local community state (nothing leaves the page) ─────────── */

type Reply = { by: string; at: Date; text: string };
function useCommunity() {
  const [makes, setMakes] = useState<Make[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [deleted, setDeleted] = useState<Set<string>>(new Set());
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [makeComments, setMakeComments] = useState<Record<string, Reply[]>>({});
  const [newQs, setNewQs] = useState<Question[]>([]);
  const [voted, setVoted] = useState<Set<string>>(new Set());
  const [resolved, setResolved] = useState<Record<string, boolean>>({});
  const [answers, setAnswers] = useState<Record<string, Reply>>({});
  const [qReplies, setQReplies] = useState<Record<string, Reply[]>>({});
  const [msgs, setMsgs] = useState<Message[]>([]);
  const [reacted, setReacted] = useState<Record<string, string[]>>({});
  const [pinned, setPinned] = useState<string | null>(null);
  const toggle = (set: (f: (s: Set<string>) => Set<string>) => void, id: string) => set(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return {
    makes, hidden, deleted, liked, makeComments, newQs, voted, resolved, answers, qReplies, msgs, reacted, pinned,
    addMake: (m: Make) => setMakes(x => [m, ...x]),
    toggleHidden: (id: string) => toggle(setHidden, id),
    remove: (id: string) => setDeleted(s => new Set(s).add(id)),
    restore: (id: string) => setDeleted(s => { const n = new Set(s); n.delete(id); return n; }),
    toggleLike: (id: string) => toggle(setLiked, id),
    commentMake: (id: string, r: Reply) => setMakeComments(x => ({ ...x, [id]: [...(x[id] ?? []), r] })),
    addQ: (q: Question) => setNewQs(x => [q, ...x]),
    toggleVote: (id: string) => toggle(setVoted, id),
    setResolved: (id: string, v: boolean) => setResolved(x => ({ ...x, [id]: v })),
    answer: (id: string, r: Reply) => { setAnswers(x => ({ ...x, [id]: r })); setResolved(x => ({ ...x, [id]: true })); },
    replyQ: (id: string, r: Reply) => setQReplies(x => ({ ...x, [id]: [...(x[id] ?? []), r] })),
    post: (m: Message) => setMsgs(x => [...x, m]),
    react: (id: string, e: string) => setReacted(x => { const cur = x[id] ?? []; return { ...x, [id]: cur.includes(e) ? cur.filter(y => y !== e) : [...cur, e] }; }),
    pin: (id: string | null) => setPinned(id),
  };
}
type Cm = ReturnType<typeof useCommunity>;

function useWide() {
  const q = "(min-width: 900px)";
  const [w, setW] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q); const f = () => setW(m.matches);
    m.addEventListener("change", f); return () => m.removeEventListener("change", f);
  }, []);
  return w;
}

const projectName = (id: string) => (id === GENERAL_SEWING ? "General sewing" : getProject(id)?.title ?? id);

/* ─────────── Screen ─────────── */

export function PortalCommunity() {
  const { role, now, can } = usePortal();
  const [sp, setSp] = useSearchParams();
  const wide = useWide();
  const cm = useCommunity();
  const [toast, show] = useToast();
  const [share, setShare] = useState(false);
  const [ask, setAsk] = useState(false);
  const [status, setStatus] = useState<"open" | "answered" | "all">("all");

  const ch: Ch = isCh(sp.get("ch")) ? (sp.get("ch") as Ch) : "makes";
  const project = sp.get("project") ?? "all";
  const qId = sp.get("q");
  const setParams = (patch: Record<string, string | null>, push = false) => setSp(prev => {
    const n = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(patch)) { if (v == null) n.delete(k); else n.set(k, v); }
    return n;
  }, { replace: !push, preventScrollReset: true });
  const go = (c: Ch) => { setParams({ ch: c, q: null, project: c === "general" ? null : project === GENERAL_SEWING && c === "makes" ? null : sp.get("project") }); };

  /* Derived lists */
  const me = role === "chelsea" ? "chelsea" : "me";
  const isChelsea = role === "chelsea";
  const allMakes = useMemo(() => [...cm.makes.filter(m => m.at <= now || m.id.startsWith("local")), ...makesAt(now)]
    .filter(m => !cm.deleted.has(m.id) && (isChelsea || !cm.hidden.has(m.id))), [cm.makes, cm.deleted, cm.hidden, now, isChelsea]);
  const makes = project === "all" || project === GENERAL_SEWING ? allMakes : allMakes.filter(m => m.projectId === project);
  const qs = useMemo(() => [...cm.newQs, ...QA, ...EXTRA_QS].filter(q => (q.at <= now || q.id.startsWith("local")) && !cm.deleted.has(q.id)).map(q => {
    const answer = cm.answers[q.id] ?? q.answer;
    const res = cm.resolved[q.id] ?? q.resolved ?? false;
    return { ...q, answer, resolved: res, votes: q.votes + (cm.voted.has(q.id) ? 1 : 0), replies: [...(q.replies ?? []), ...(cm.qReplies[q.id] ?? [])] };
  }).sort((a, b) => Number(!!a.resolved) - Number(!!b.resolved) || b.at.getTime() - a.at.getTime()), [cm.newQs, cm.deleted, cm.answers, cm.resolved, cm.voted, cm.qReplies, now]);
  const qsByProject = project === "all" ? qs : qs.filter(q => q.projectId === project);
  const openCount = qs.filter(q => !q.resolved).length;
  const qsShown = qsByProject.filter(q => status === "all" || (status === "open" ? !q.resolved : q.resolved));
  const general = [...messagesAt(now, "general"), ...cm.msgs].filter(m => !cm.deleted.has(m.id) && (isChelsea || !cm.hidden.has(m.id)));
  const newGeneral = general.filter(m => m.at > ME.lastVisit && m.by !== me).length;
  const thread = qs.find(q => q.id === qId) ?? (wide && ch === "help" ? qsShown[0] : undefined);

  const counts: Record<Ch, ReactNode> = { makes: allMakes.length, help: openCount ? `${openCount} open` : "", general: newGeneral ? `${newGeneral} new` : "" };

  /* Visitor: everything is behind a free account */
  if (!can("communityRead")) return <PortalShell active="community" title="Community" subtitle="Show your makes, get unstuck, say hi.">
    <Locked makes={allMakes} qCount={qs.length} />
  </PortalShell>;

  const actions = ch === "makes" ? (can("makesPost") ? <Btn icon={<ImagePlus size={18} aria-hidden="true" />} onClick={() => setShare(true)}>Share a make</Btn> : null)
    : ch === "help" ? (can("qaAsk") && !isChelsea ? <Btn icon={<Plus size={18} aria-hidden="true" />} onClick={() => setAsk(true)}>Ask a question</Btn> : null) : null;

  const openThread = (id: string) => { setParams({ q: id }, !wide); if (!wide) window.scrollTo({ top: 0 }); };

  const helpFilters = <div className="ptc-filters">
    <Segmented size="sm" block={!wide} label="Question status" value={status} onChange={setStatus}
      options={[{ value: "all", label: "All", count: qsByProject.length }, { value: "open", label: "Open", count: qsByProject.filter(q => !q.resolved).length }, { value: "answered", label: "Answered", count: qsByProject.filter(q => q.resolved).length }]} />
    <ProjectFilter value={project} onChange={v => setParams({ project: v === "all" ? null : v, q: null })} withGeneral
      counts={Object.fromEntries([...new Set(qs.map(q => q.projectId))].map(id => [id, qs.filter(q => q.projectId === id).length]))} total={qs.length} />
  </div>;

  const threadList = qsShown.length
    ? <List label="Questions">{qsShown.map(q => <ThreadRow key={q.id} q={q} now={now} active={wide && thread?.id === q.id} onOpen={() => openThread(q.id)} />)}</List>
    : <Empty icon={<CircleHelp size={22} />} title={status === "open" ? "Nothing waiting" : "No questions yet"} hand={status === "open" ? "all caught up!" : undefined}
        body={status === "open" ? "Every question here has an answer." : "Stuck on something? Ask and Chelsea or another member will help."} />;

  let pane: ReactNode;
  if (ch === "makes") pane = <MakesPane makes={makes} project={project} cm={cm} now={now} qCount={qs.filter(q => q.projectId === project).length}
    onShare={() => setShare(true)} onProject={v => setParams({ project: v === "all" ? null : v })} wide={wide} show={show} allMakes={allMakes} />;
  else if (ch === "help") pane = wide
    ? (thread ? <Thread key={thread.id} q={thread} cm={cm} now={now} show={show} me={me} keep={() => setParams({ q: thread.id })} /> : <Empty icon={<CircleHelp size={22} />} title="Pick a question" body="Choose a thread on the left." />)
    : thread
      ? <Thread key={thread.id} q={thread} cm={cm} now={now} show={show} me={me} />
      : <div className="ptc-stack"><PaneHead ch="help" />{helpFilters}{!can("qaAsk") && <div className="ptc-lockrow"><span>Ask Chelsea with</span><TierLock thing="qaAsk" variant="inline" /></div>}{threadList}</div>;
  else pane = <General msgs={general} cm={cm} now={now} show={show} me={me} />;

  const phoneThread = !wide && ch === "help" && !!thread;
  return <PortalShell active="community" title={phoneThread ? undefined : "Community"} subtitle={phoneThread ? undefined : "Show your makes, get unstuck, say hi."}
    back={phoneThread ? { to: `${P.community}?ch=help${project !== "all" ? `&project=${project}` : ""}`, label: "#help" } : undefined} actions={wide ? actions : undefined}>
    {toast}
    {isChelsea && <ModStrip open={openCount} onOpen={() => { setStatus("open"); setParams({ ch: "help", q: null, project: null }); }} />}
    <div className="ptc">
      {wide ? <aside className="ptc-nav" aria-label="Channels">
        <List label="Channels">
          {CHANNELS.map(c => <Row key={c.id} active={ch === c.id} onClick={() => go(c.id)} chevron={false}
            lead={<span className={`ptc-chico${ch === c.id ? " is-on" : ""}`}>{CH_ICON[c.id]}</span>}
            title={`#${c.name}`} meta={c.about} trail={counts[c.id] ? <span className="ptc-count">{counts[c.id]}</span> : undefined} />)}
        </List>
        {ch === "makes" && <ProjectNav value={project} makes={allMakes} onChange={v => setParams({ project: v === "all" ? null : v })} />}
        {ch === "help" && <div className="ptc-stack">{helpFilters}{!can("qaAsk") && <div className="ptc-lockrow"><span>Ask Chelsea with</span><TierLock thing="qaAsk" variant="inline" /></div>}{threadList}</div>}
        {ch === "general" && <Card tone="paper" pad="md" className="ptc-rules">
          <p className="pt-kicker">House rules</p>
          <ul><li>Be kind; everyone started somewhere.</li><li>Sewing questions go in #help so they don't get lost.</li><li>No selling or links to other clubs.</li></ul>
        </Card>}
      </aside> : !(ch === "help" && thread) && <div className="ptc-tabs">
        {!(ch === "help" && thread) && <Segmented block label="Channels" value={ch} onChange={go}
          options={CHANNELS.map(c => ({ value: c.id, label: CH_SHORT[c.id], count: c.id === "makes" ? allMakes.length : c.id === "help" ? openCount || undefined : newGeneral || undefined }))} />}
        {ch !== "help" && actions && <div className="ptc-tabs-act">{actions}</div>}
        {ch === "help" && !thread && actions && <div className="ptc-tabs-act">{actions}</div>}
      </div>}
      <div className="ptc-pane">{pane}</div>
    </div>

    <ShareSheet open={share} onClose={() => setShare(false)} now={now} defaultProject={project !== "all" && project !== GENERAL_SEWING ? project : "witch-hat"}
      onShare={m => { cm.addMake({ ...m, by: me }); setShare(false); setParams({ ch: "makes", project: null }); show("Shared! Your make is at the top of #show-your-makes. (Prototype: only on this screen.)"); }} />
    <AskSheet open={ask} onClose={() => setAsk(false)} defaultProject={project !== "all" ? project : "witch-hat"}
      onAsk={q => { const id = `local-q-${Date.now()}`; cm.addQ({ ...q, id, by: me, at: now, votes: 0 }); setAsk(false); setStatus("all"); setParams({ ch: "help", q: id, project: null }, !wide); show("Question posted. Chelsea will see it in her answer queue."); }} />
  </PortalShell>;
}

/* ─────────── Pieces ─────────── */

function PaneHead({ ch }: { ch: Ch }) {
  const c = CHANNELS.find(x => x.id === ch)!;
  return <p className="ptc-panehead"><span className="ptc-panehead-name">#{c.name}</span><span>{c.about}</span></p>;
}

function ModStrip({ open, onOpen }: { open: number; onOpen: () => void }) {
  return <div className="ptc-mod" role="region" aria-label="Moderation">
    <ShieldCheck size={18} aria-hidden="true" />
    <p><b>Moderation</b> · {open ? <button type="button" onClick={onOpen}>{open} question{open > 1 ? "s need" : " needs"} an answer</button> : "All questions answered"} · 0 reports · Word filter on</p>
  </div>;
}

function ProjectFilter({ value, onChange, counts, total, withGeneral }: { value: string; onChange: (v: string) => void; counts: Record<string, number>; total: number; withGeneral?: boolean }) {
  const ids = PROJECTS.map(p => p.id).filter(id => counts[id]);
  if (withGeneral && counts[GENERAL_SEWING]) ids.push(GENERAL_SEWING);
  return <Segmented size="sm" label="Project" value={value} onChange={onChange}
    options={[{ value: "all", label: "All projects", count: total }, ...ids.map(id => ({ value: id, label: projectName(id), count: counts[id] }))]} />;
}

function ProjectNav({ value, makes, onChange }: { value: string; makes: Make[]; onChange: (v: string) => void }) {
  const ids = PROJECTS.map(p => p.id).filter(id => makes.some(m => m.projectId === id));
  return <section aria-label="Filter by project" className="ptc-stack">
    <p className="pt-kicker ptc-navlabel">By project</p>
    <List>
      <Row active={value === "all"} onClick={() => onChange("all")} chevron={false} title="All makes" trail={<span className="ptc-count">{makes.length}</span>}
        lead={<span className="ptc-chico"><Sparkles size={18} aria-hidden="true" /></span>} />
      {ids.map(id => {
        const p = getProject(id)!;
        return <Row key={id} active={value === id} onClick={() => onChange(id)} chevron={false} title={p.title} meta={p.monthLabel}
          lead={<Thumb src={p.hero} ratio="1/1" width={40} />} trail={<span className="ptc-count">{makes.filter(m => m.projectId === id).length}</span>} />;
      })}
    </List>
  </section>;
}

/* ─────────── Visitor lock ─────────── */

function Locked({ makes, qCount }: { makes: Make[]; qCount: number }) {
  return <div className="ptc-locked">
    <div className="ptc-peekwrap">
      <div className="ptc-peek" aria-hidden="true">
        {makes.slice(0, 6).map((m, i) => <Polaroid key={m.id} src={m.img} caption={m.caption} tilt={TILTS[i % TILTS.length]} />)}
      </div>
      {/* Same markup as the kit's overlay lock, but the line names "Free account" rather than just "Free". */}
      <div className="pt-lock pt-lock-overlay">
        <div className="pt-lock-inner">
          <span className="pt-lock-icon"><Lock size={18} aria-hidden="true" /></span>
          <p className="pt-lock-line">Free account · no card needed</p>
          <p className="pt-lock-title">Come see what everyone's making</p>
          <p className="pt-lock-body">{makes.length} makes and {qCount} questions so far. Reading is free with an account; sharing your own makes is for {PLANS.friend.tag} and up.</p>
          <div className="pt-lock-actions">
            <Btn to={`${P.join}?plan=free`} variant="pop">Create a free account</Btn>
            <PLink to={P.me} className="pt-lock-link">Already a member? Sign in</PLink>
          </div>
        </div>
      </div>
    </div>
    <ul className="ptc-locked-ch">
      {CHANNELS.map(c => <li key={c.id}><span className="ptc-chico">{CH_ICON[c.id]}</span><span><b>#{c.name}</b><small>{c.about}</small></span></li>)}
    </ul>
  </div>;
}

/* ─────────── #show-your-makes ─────────── */

function MakesPane({ makes, allMakes, project, cm, now, qCount, onShare, onProject, wide, show }: {
  makes: Make[]; allMakes: Make[]; project: string; cm: Cm; now: Date; qCount: number; onShare: () => void; onProject: (v: string) => void; wide: boolean; show: (t: ReactNode) => void;
}) {
  const { can, role } = usePortal();
  const [open, setOpen] = useState<string | null>(null);
  const p = getProject(project);
  const sel = allMakes.find(m => m.id === open);
  const counts = Object.fromEntries(PROJECTS.map(x => [x.id, allMakes.filter(m => m.projectId === x.id).length]));
  return <div className="ptc-stack">
    {!wide && <PaneHead ch="makes" />}
    {!wide && <ProjectFilter value={project} onChange={onProject} counts={counts} total={allMakes.length} />}
    {p && <Card pad="sm" className="ptc-projcard">
      <Thumb src={p.hero} alt={p.heroAlt} ratio="1/1" width={56} />
      <div className="ptc-projcard-main"><p className="pt-kicker">{p.monthLabel}</p><p className="ptc-projcard-t">{p.title}</p></div>
      <div className="ptc-projcard-links">
        <PLink to={`${P.community}?ch=help&project=${p.id}`}>Q&A{qCount ? ` (${qCount})` : ""}</PLink>
        <PLink to={P.project(p.id)}>Project</PLink>
      </div>
    </Card>}
    {!can("makesPost") && <div className="ptc-lockrow"><span>Share your own makes with</span><TierLock thing="makesPost" variant="inline" /></div>}
    {makes.length ? <ul className="ptc-grid">
      {makes.map((m, i) => {
        const likes = m.likes + (cm.liked.has(m.id) ? 1 : 0);
        const isNew = m.at > ME.lastVisit && m.by !== "me";
        return <li key={m.id} className={cm.hidden.has(m.id) ? "is-hidden" : ""}>
          <button type="button" className="ptc-make" onClick={() => setOpen(m.id)} aria-label={`${m.caption}, by ${nameOf(m.by, role)}`}>
            <Polaroid src={m.img} caption={m.caption} tilt={TILTS[i % TILTS.length]} tape={i % 3 === 0 ? TAPES[(i / 3) % 4] : undefined}
              by={<span className="ptc-make-by"><span>{nameOf(m.by, role)}{project === "all" ? ` · ${getProject(m.projectId)?.title}` : ""}</span><span className="ptc-make-likes"><Heart size={12} aria-hidden="true" fill={cm.liked.has(m.id) ? "currentColor" : "none"} />{likes}</span></span>} />
            {isNew && <Chip tone="gold" className="ptc-make-new">New</Chip>}
            {cm.hidden.has(m.id) && <Chip tone="lock" className="ptc-make-new" icon={<EyeOff size={12} aria-hidden="true" />}>Hidden</Chip>}
          </button>
        </li>;
      })}
    </ul> : <Empty icon={<Camera size={22} />} hand="be the first!" title={`No ${p?.title ?? ""} makes yet`.replace("  ", " ")}
      body="Finished, half-done or just a fabric pull: it all counts."
      action={can("makesPost") ? <Btn onClick={onShare} icon={<ImagePlus size={18} aria-hidden="true" />}>Share a make</Btn> : undefined} />}

    <Sheet open={!!sel} onClose={() => setOpen(null)} title={sel ? `${nameOf(sel.by, role) === "You" ? "Your" : `${nameOf(sel.by, role)}'s`} make` : ""}
      footer={sel && <MakeActions m={sel} cm={cm} show={show} onClose={() => setOpen(null)} role={role} />}>
      {sel && <MakeDetail m={sel} cm={cm} now={now} />}
    </Sheet>
  </div>;
}
/** "You" for the signed-in member; Chelsea sees the sample member by name. */
const nameOf = (id: string, role?: string) => (id === "me" ? (role === "chelsea" ? ME.name : "You") : who(id).name);
/** Name with badges, but the sample member reads as "Rosie H." (not "(you)") in Chelsea's view. */
function Who({ id, badge }: { id: string; badge?: boolean }) {
  const { role } = usePortal();
  return <Name person={id === "me" && role === "chelsea" ? { ...who("me"), id: "rosie" } : id} badge={badge} />;
}

function MakeDetail({ m, cm, now }: { m: Make; cm: Cm; now: Date }) {
  const { can } = usePortal();
  const [text, setText] = useState("");
  const p = getProject(m.projectId);
  const comments = [...(MAKE_COMMENTS[m.id] ?? []).map(c => ({ by: c.by, text: c.text, at: plus(m.at, c.min) })).filter(c => c.at <= now), ...(cm.makeComments[m.id] ?? [])];
  const liked = cm.liked.has(m.id);
  return <div className="ptc-md">
    <div className="ptc-md-img"><img src={m.img} alt={m.caption} /></div>
    <Hand className="ptc-md-cap">{m.caption}</Hand>
    <div className="ptc-md-by">
      <Avatar person={m.by === "me" ? ME : m.by} size={36} />
      <span className="ptc-md-who"><Who id={m.by} /><small>{ago(m.at, now)}</small></span>
      {can("communityPost")
        ? <button type="button" className={`ptc-like${liked ? " is-on" : ""}`} aria-pressed={liked} onClick={() => cm.toggleLike(m.id)}>
            <Heart size={18} fill={liked ? "currentColor" : "none"} aria-hidden="true" />{m.likes + (liked ? 1 : 0)}</button>
        : <span className="ptc-like is-static"><Heart size={18} aria-hidden="true" />{m.likes}</span>}
    </div>
    {p && <List><Row lead={<Thumb src={p.hero} ratio="1/1" width={44} />} title={p.title} meta={`${p.monthLabel} · see the project`} to={P.project(p.id)} /></List>}
    <div className="ptc-comments">
      <p className="pt-kicker">Comments</p>
      {comments.length ? comments.map((c, i) => <div key={i} className="ptc-comment">
        <Avatar person={c.by === "me" ? ME : c.by} size={28} />
        <p><Who id={c.by} /> <span className="ptc-time">{ago(c.at, now)}</span><br />{c.text}</p>
      </div>) : <p className="ptc-muted">No comments yet.</p>}
      {can("communityPost")
        ? <form className="ptc-composer is-small" onSubmit={e => { e.preventDefault(); if (!text.trim()) return; cm.commentMake(m.id, { by: "me", at: now, text: text.trim() }); setText(""); }}>
            <input value={text} onChange={e => setText(e.target.value)} placeholder="Say something nice…" aria-label="Write a comment" />
            <IconBtn label="Post comment" icon={<Send size={18} />} onClick={() => { if (!text.trim()) return; cm.commentMake(m.id, { by: "me", at: now, text: text.trim() }); setText(""); }} />
          </form>
        : <TierLock thing="communityPost" variant="inline" />}
    </div>
  </div>;
}

function MakeActions({ m, cm, show, onClose, role }: { m: Make; cm: Cm; show: (t: ReactNode) => void; onClose: () => void; role: string }) {
  if (role !== "chelsea") return <div className="ptc-acts">
    <Btn variant="ghost" size="sm" icon={<Link2 size={16} aria-hidden="true" />} onClick={() => show("Link copied.")}>Copy link</Btn>
    <Btn variant="ghost" size="sm" icon={<Flag size={16} aria-hidden="true" />} onClick={() => show("Thanks. Chelsea will take a look. (Prototype: nothing was sent.)")}>Report</Btn>
  </div>;
  return <div className="ptc-acts">
    <Btn variant="secondary" size="sm" icon={<Star size={16} aria-hidden="true" />} onClick={() => show("Featured on Home for everyone this week. (Prototype)")}>Feature on Home</Btn>
    <Btn variant="secondary" size="sm" icon={<EyeOff size={16} aria-hidden="true" />} onClick={() => { cm.toggleHidden(m.id); show(cm.hidden.has(m.id) ? "Visible to members again." : "Hidden from members. Only you can see it now."); }}>{cm.hidden.has(m.id) ? "Unhide" : "Hide"}</Btn>
    <Btn variant="ghost" size="sm" icon={<Trash2 size={16} aria-hidden="true" />} onClick={() => { cm.remove(m.id); onClose(); show(<>Make deleted. <button type="button" className="ptc-undo" onClick={() => cm.restore(m.id)}>Undo</button></>); }}>Delete</Btn>
  </div>;
}

/* ─────────── #help ─────────── */

type Q = Question & { replies: NonNullable<Question["replies"]> };

function ThreadRow({ q, now, active, onOpen }: { q: Q; now: Date; active?: boolean; onOpen: () => void }) {
  const { role } = usePortal();
  const byChelsea = q.answer?.by === "chelsea";
  const n = q.replies.length + (q.answer ? 1 : 0);
  return <Row active={active} onClick={onOpen}
    lead={q.resolved ? <span className="ptc-status is-ok" title={byChelsea ? "Answered by Chelsea" : "Resolved"}><CheckCircle2 size={20} aria-label="Answered" /></span>
      : <span className="ptc-status is-open" title="Needs an answer"><CircleHelp size={20} aria-label="Open" /></span>}
    title={q.text}
    meta={<>{q.by === "me" && role !== "chelsea" ? "You" : <Who id={q.by} badge={false} />} · {projectName(q.projectId)} · {ago(q.at, now)}{n ? ` · ${n} repl${n > 1 ? "ies" : "y"}` : ""}{byChelsea && <> · <span className="ptc-by-chelsea">Chelsea answered</span></>}</>}
    trail={<span className="ptc-votes" aria-label={`${q.votes} me too`}>+{q.votes}</span>} />;
}

function Thread({ q, cm, now, show, me, keep }: { q: Q; cm: Cm; now: Date; show: (t: ReactNode) => void; me: string; keep?: () => void }) {
  const { can, role } = usePortal();
  const [text, setText] = useState("");
  const isChelsea = role === "chelsea";
  const s = q.sessionId ? getSession(q.sessionId) : undefined;
  const p = getProject(q.projectId);
  const voted = cm.voted.has(q.id);
  const send = (asAnswer: boolean) => {
    const t = text.trim(); if (!t) return;
    keep?.(); // the list re-sorts (answered sink); stay on this thread
    if (asAnswer) { cm.answer(q.id, { by: "chelsea", at: now, text: t }); show(`Answered and marked resolved. ${nameOf(q.by, role)} gets a notification.`); }
    else cm.replyQ(q.id, { by: me, at: now, text: t });
    setText("");
  };
  return <article className="ptc-thread" aria-label="Question">
    <div className="ptc-thread-tags">
      {q.resolved ? <Chip tone="ok" icon={<CheckCircle2 size={13} aria-hidden="true" />}>{q.answer?.by === "chelsea" ? "Answered by Chelsea" : "Resolved"}</Chip>
        : <Chip tone="gold" icon={<CircleHelp size={13} aria-hidden="true" />}>Needs an answer</Chip>}
      {p ? <PLink to={P.project(p.id)} className="ptc-tag">{p.title}</PLink> : <span className="ptc-tag is-static">General sewing</span>}
      {s && q.t != null && <PLink to={`${P.session(s.id)}#t=${q.t}`} className="ptc-tag is-time"><PlayCircle size={14} aria-hidden="true" />{fmtT(q.t)} in {s.label}</PLink>}
    </div>
    <header className="ptc-q">
      <Avatar person={q.by === "me" ? ME : q.by} size={40} />
      <div><p className="ptc-q-who"><Who id={q.by} /> <span className="ptc-time">{fmtDay(q.at, "America/Denver", { weekday: "short", month: "short", day: "numeric" })} · {ago(q.at, now)}</span></p>
        <h2 className="ptc-q-text">{q.text}</h2></div>
    </header>
    <div className="ptc-q-acts">
      {can("communityPost")
        ? <button type="button" className={`ptc-metoo${voted ? " is-on" : ""}`} aria-pressed={voted} onClick={() => cm.toggleVote(q.id)}>
            <Plus size={15} aria-hidden="true" />Me too · {q.votes}</button>
        : <span className="ptc-metoo is-static">{q.votes} people want to know</span>}
      <Btn variant="ghost" size="sm" icon={<Link2 size={15} aria-hidden="true" />} onClick={() => show("Link to this question copied.")}>Copy link</Btn>
    </div>

    {q.answer && <section className={`ptc-answer${q.answer.by === "chelsea" ? " is-chelsea" : ""}`} aria-label="Answer">
      <p className="ptc-answer-label"><CheckCircle2 size={16} aria-hidden="true" />{q.answer.by === "chelsea" ? "Chelsea's answer" : "Answer"}</p>
      <div className="ptc-answer-body">
        <Avatar person={q.answer.by} size={36} />
        <div><p className="ptc-q-who"><Who id={q.answer.by} /> <span className="ptc-time">{ago(q.answer.at, now)}</span></p>
          <p className="ptc-answer-text">{q.answer.text}</p></div>
      </div>
    </section>}

    {q.replies.length > 0 && <section className="ptc-replies" aria-label="Replies">
      <p className="pt-kicker">{q.replies.length} repl{q.replies.length > 1 ? "ies" : "y"}</p>
      {q.replies.map((r, i) => <div key={i} className="ptc-reply">
        <Avatar person={r.by === "me" ? ME : r.by} size={32} />
        <div><p className="ptc-q-who"><Who id={r.by} /> <span className="ptc-time">{ago(r.at, now)}</span></p><p>{r.text}</p></div>
      </div>)}
    </section>}

    {!q.answer && !q.replies.length && <p className="ptc-noreply"><CornerDownRight size={16} aria-hidden="true" />No replies yet. Chelsea answers within a day or two; know the answer? Jump in.</p>}

    {can("communityPost") ? <form className="ptc-composer" onSubmit={e => { e.preventDefault(); send(false); }}>
      <textarea rows={isChelsea ? 3 : 2} value={text} onChange={e => setText(e.target.value)} placeholder={isChelsea ? "Write an answer…" : "Write a reply…"} aria-label={isChelsea ? "Write an answer" : "Write a reply"} />
      <div className="ptc-composer-act">
        {isChelsea && !q.answer && <Btn size="sm" icon={<CheckCircle2 size={16} aria-hidden="true" />} onClick={() => send(true)} disabled={!text.trim()}>Answer & resolve</Btn>}
        <Btn size="sm" variant={isChelsea && !q.answer ? "secondary" : "primary"} type="submit" icon={<Send size={15} aria-hidden="true" />} disabled={!text.trim()}>Reply</Btn>
      </div>
    </form> : <div className="ptc-lockrow"><span>Reply with</span><TierLock thing="communityPost" variant="inline" /></div>}

    {isChelsea && <div className="ptc-modrow" aria-label="Moderation">
      <span className="pt-kicker">Moderate</span>
      <Btn size="sm" variant="quiet" icon={<CheckCircle2 size={15} aria-hidden="true" />} onClick={() => { keep?.(); cm.setResolved(q.id, !q.resolved); }}>{q.resolved ? "Reopen" : "Mark resolved"}</Btn>
      {s && q.t != null && <Btn size="sm" variant="quiet" icon={<PlayCircle size={15} aria-hidden="true" />} onClick={() => show(`Added as a fix note at ${fmtT(q.t!)} on ${s.title}. Members see it in the player.`)}>Make fix note</Btn>}
      <Btn size="sm" variant="quiet" icon={<Pin size={15} aria-hidden="true" />} onClick={() => show(`Pinned to the ${p?.title ?? "help"} Q&A.`)}>Pin to project</Btn>
      <Btn size="sm" variant="quiet" icon={<Trash2 size={15} aria-hidden="true" />} onClick={() => { cm.remove(q.id); show(<>Question deleted. <button type="button" className="ptc-undo" onClick={() => cm.restore(q.id)}>Undo</button></>); }}>Delete</Btn>
    </div>}
  </article>;
}

/* ─────────── #general ─────────── */

const EMOJI = ["❤️", "😍", "🎉", "🙌", "😂"];

function General({ msgs, cm, now, show, me }: { msgs: Message[]; cm: Cm; now: Date; show: (t: ReactNode) => void; me: string }) {
  const { can, role } = usePortal();
  const [text, setText] = useState("");
  const [openReplies, setOpenReplies] = useState<Set<string>>(new Set());
  const endRef = useRef<HTMLDivElement>(null);
  const pinned = msgs.find(m => m.id === cm.pinned) ?? null;
  const firstNew = msgs.find(m => m.at > ME.lastVisit && m.by !== me)?.id;
  let lastDay = "";
  const send = () => {
    const t = text.trim(); if (!t) return;
    cm.post({ id: `local-m-${Date.now()}`, channel: "general", by: me, at: now, text: t });
    setText(""); window.setTimeout(() => endRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 30);
  };
  return <div className="ptc-stack">
    <PaneHead ch="general" />
    {pinned && <div className="ptc-pinned"><Pin size={15} aria-hidden="true" /><p><b>Pinned · <Who id={pinned.by} badge={false} /></b> {pinned.text}</p>
      {role === "chelsea" && <IconBtn label="Unpin" icon={<Undo2 size={16} />} size={36} onClick={() => cm.pin(null)} />}</div>}
    <div className="ptc-stream" role="log" aria-label="#general messages">
      {msgs.map(m => {
        const day = fmtDay(m.at, "America/Denver", { weekday: "long", month: "short", day: "numeric" });
        const sep = day !== lastDay ? <p className="ptc-day" key={`d-${m.id}`}><span>{day}</span></p> : null;
        lastDay = day;
        const mine = cm.reacted[m.id] ?? [];
        const reactions = { ...(m.reactions ?? {}) };
        for (const e of mine) reactions[e] = (reactions[e] ?? 0) + 1;
        const replies = (REPLIES[m.id] ?? []).map(r => ({ ...r, at: plus(m.at, r.min) })).filter(r => r.at <= now);
        const rOpen = openReplies.has(m.id);
        return <div key={m.id} className="ptc-msgwrap">
          {sep}
          {m.id === firstNew && <p className="ptc-new"><span>New since your last visit</span></p>}
          <div className={`ptc-msg${cm.hidden.has(m.id) ? " is-hidden" : ""}`}>
            <Avatar person={m.by === "me" ? ME : m.by} size={36} />
            <div className="ptc-msg-main">
              <p className="ptc-q-who"><Who id={m.by} /> <span className="ptc-time">{ago(m.at, now)}</span>{cm.hidden.has(m.id) && <Chip tone="lock" icon={<EyeOff size={12} aria-hidden="true" />}>Hidden</Chip>}</p>
              <p className="ptc-msg-text">{m.text}</p>
              {m.img && <img className="ptc-msg-img" src={m.img} alt="" loading="lazy" />}
              <div className="ptc-reacts">
                {Object.entries(reactions).map(([e, n]) => can("communityPost")
                  ? <button key={e} type="button" className={`ptc-react${mine.includes(e) ? " is-on" : ""}`} aria-pressed={mine.includes(e)} onClick={() => cm.react(m.id, e)}><span>{e}</span>{n}</button>
                  : <span key={e} className="ptc-react is-static"><span>{e}</span>{n}</span>)}
                {can("communityPost") && <ReactPicker onPick={e => cm.react(m.id, e)} />}
                {replies.length > 0 && <button type="button" className="ptc-replies-btn" aria-expanded={rOpen} onClick={() => setOpenReplies(s => { const n = new Set(s); if (n.has(m.id)) n.delete(m.id); else n.add(m.id); return n; })}>
                  <CornerDownRight size={14} aria-hidden="true" />{rOpen ? "Hide replies" : `${replies.length} repl${replies.length > 1 ? "ies" : "y"}`}</button>}
              </div>
              {rOpen && <div className="ptc-thread-replies">{replies.map((r, i) => <div key={i} className="ptc-reply is-small">
                <Avatar person={r.by} size={26} /><div><p className="ptc-q-who"><Who id={r.by} /> <span className="ptc-time">{ago(r.at, now)}</span></p><p>{r.text}</p></div>
              </div>)}</div>}
            </div>
            <MsgMenu m={m} cm={cm} show={show} />
          </div>
        </div>;
      })}
      <div ref={endRef} />
    </div>
    {can("communityPost")
      ? <form className="ptc-composer" onSubmit={e => { e.preventDefault(); send(); }}>
          <textarea rows={2} value={text} onChange={e => setText(e.target.value)} placeholder="Message #general" aria-label="Message #general"
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && window.innerWidth >= 900) { e.preventDefault(); send(); } }} />
          <div className="ptc-composer-act">
            <span className="ptc-muted ptc-hint">Sewing question? <PLink to={`${P.community}?ch=help`}>Ask in #help</PLink></span>
            <Btn size="sm" type="submit" icon={<Send size={15} aria-hidden="true" />} disabled={!text.trim()}>Send</Btn>
          </div>
        </form>
      : <div className="ptc-lockrow"><span>Post and react with</span><TierLock thing="communityPost" variant="inline" /></div>}
  </div>;
}

function ReactPicker({ onPick }: { onPick: (e: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", off); return () => document.removeEventListener("pointerdown", off);
  }, [open]);
  return <span className="ptc-pick" ref={ref}>
    <button type="button" className="ptc-react is-add" aria-label="Add a reaction" aria-expanded={open} onClick={() => setOpen(o => !o)}><Smile size={15} aria-hidden="true" /><Plus size={11} aria-hidden="true" /></button>
    {open && <span className="ptc-pick-menu" role="menu">{EMOJI.map(e => <button key={e} type="button" role="menuitem" onClick={() => { onPick(e); setOpen(false); }} aria-label={`React ${e}`}>{e}</button>)}</span>}
  </span>;
}

function MsgMenu({ m, cm, show }: { m: Message; cm: Cm; show: (t: ReactNode) => void }) {
  const { role } = usePortal();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", off); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", off); document.removeEventListener("keydown", esc); };
  }, [open]);
  const item = (icon: ReactNode, label: string, fn: () => void) => <button type="button" role="menuitem" onClick={() => { fn(); setOpen(false); }}>{icon}{label}</button>;
  const chelsea = role === "chelsea";
  return <div className="ptc-menu" ref={ref}>
    <IconBtn label="Message options" icon={<MoreHorizontal size={18} />} size={36} onClick={() => setOpen(o => !o)} />
    {open && <div className="ptc-menu-list" role="menu">
      {chelsea ? <>
        {item(<Pin size={15} />, cm.pinned === m.id ? "Unpin" : "Pin to top", () => cm.pin(cm.pinned === m.id ? null : m.id))}
        {item(<EyeOff size={15} />, cm.hidden.has(m.id) ? "Unhide" : "Hide from members", () => cm.toggleHidden(m.id))}
        {m.by !== "chelsea" && item(<ShieldCheck size={15} />, "Mute for 24 hours", () => show("Muted for 24 hours. (Prototype)"))}
        {item(<Trash2 size={15} />, "Delete", () => { cm.remove(m.id); show(<>Message deleted. <button type="button" className="ptc-undo" onClick={() => cm.restore(m.id)}>Undo</button></>); })}
      </> : <>
        {item(<Link2 size={15} />, "Copy link", () => show("Link copied."))}
        {item(<Flag size={15} />, "Report to Chelsea", () => show("Thanks. Chelsea will take a look. (Prototype: nothing was sent.)"))}
      </>}
    </div>}
  </div>;
}

/* ─────────── Share a make / Ask a question sheets ─────────── */

function ShareSheet({ open, onClose, onShare, defaultProject, now }: { open: boolean; onClose: () => void; onShare: (m: Make) => void; defaultProject: string; now: Date }) {
  const [img, setImg] = useState<string | null>(null);
  const [proj, setProj] = useState(defaultProject);
  const [cap, setCap] = useState("");
  useEffect(() => { if (open) { setProj(defaultProject); } }, [open, defaultProject]);
  const choices = PROJECTS.filter(p => p.status !== "announced").slice(0, 4);
  const submit = () => { if (!img) return; onShare({ id: `local-mk-${Date.now()}`, projectId: proj, by: "me", img, caption: cap.trim() || "My make!", at: now, likes: 0 }); setImg(null); setCap(""); };
  return <Sheet open={open} onClose={onClose} title="Share a make"
    footer={<Btn block size="lg" disabled={!img} onClick={submit} icon={<Send size={17} aria-hidden="true" />}>Share to #show-your-makes</Btn>}>
    <div className="ptc-form">
      <fieldset>
        <legend>Photo</legend>
        <div className="ptc-photos">
          {SAMPLE_PHOTOS.map(src => <label key={src} className={`ptc-photo${img === src ? " is-on" : ""}`}>
            <input type="radio" name="make-photo" checked={img === src} onChange={() => setImg(src)} /><img src={src} alt="" />
          </label>)}
          <label className="ptc-photo is-upload">
            <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) setImg(URL.createObjectURL(f)); }} />
            {img && !SAMPLE_PHOTOS.includes(img) ? <img src={img} alt="Your photo" /> : <><Camera size={20} aria-hidden="true" /><span>Upload</span></>}
          </label>
        </div>
        <p className="ptc-muted">Sample photos for the prototype. Uploads stay in this browser.</p>
      </fieldset>
      <fieldset>
        <legend>Project</legend>
        <Segmented size="sm" label="Project" value={proj} onChange={setProj} options={choices.map(p => ({ value: p.id, label: p.title }))} />
      </fieldset>
      <label className="ptc-field"><span>Caption</span>
        <input value={cap} maxLength={120} onChange={e => setCap(e.target.value)} placeholder="Brim done, binding tomorrow!" /></label>
    </div>
  </Sheet>;
}

function AskSheet({ open, onClose, onAsk, defaultProject }: { open: boolean; onClose: () => void; onAsk: (q: Omit<Question, "id" | "by" | "at" | "votes">) => void; defaultProject: string }) {
  const [proj, setProj] = useState(defaultProject);
  const [sess, setSess] = useState("");
  const [ts, setTs] = useState("");
  const [text, setText] = useState("");
  useEffect(() => { if (open) setProj(defaultProject === "all" ? "witch-hat" : defaultProject); }, [open, defaultProject]);
  const sessions = sessionsFor(proj).filter(s => s.replay);
  const toSec = (v: string) => { const parts = v.split(":").map(Number); return parts.some(isNaN) ? undefined : parts.reduce((a, n) => a * 60 + n, 0); };
  const submit = () => { if (!text.trim()) return; onAsk({ projectId: proj, text: text.trim(), sessionId: sess || undefined, t: sess && ts ? toSec(ts) : undefined }); setText(""); setTs(""); setSess(""); };
  return <Sheet open={open} onClose={onClose} title="Ask a question"
    footer={<Btn block size="lg" disabled={!text.trim()} onClick={submit} icon={<Send size={17} aria-hidden="true" />}>Post question</Btn>}>
    <div className="ptc-form">
      <fieldset>
        <legend>About</legend>
        <Segmented size="sm" label="Project" value={proj} onChange={v => { setProj(v); setSess(""); }}
          options={[...PROJECTS.filter(p => p.status !== "announced").slice(0, 3).map(p => ({ value: p.id, label: p.title })), { value: GENERAL_SEWING, label: "General sewing" }]} />
      </fieldset>
      {sessions.length > 0 && <div className="ptc-field-row">
        <label className="ptc-field"><span>Replay (optional)</span>
          <select value={sess} onChange={e => setSess(e.target.value)}>
            <option value="">None</option>{sessions.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select></label>
        <label className="ptc-field"><span>At (h:mm:ss)</span><input value={ts} onChange={e => setTs(e.target.value)} placeholder="1:32:00" disabled={!sess} inputMode="numeric" /></label>
      </div>}
      <label className="ptc-field"><span>Your question</span>
        <textarea rows={4} value={text} onChange={e => setText(e.target.value)} placeholder="Do the brim darts face up or down?" /></label>
      <p className="ptc-muted">Chelsea answers within a day or two and marks the answer. Others can chime in too.</p>
    </div>
  </Sheet>;
}
